// Small presentation helpers shared by the Pulsars pages. Kept in a .ts module
// so the component files export only components (react-refresh).

import type { CheckInMarker, StandingMeta } from '@shared/api-types';
import { formatDateTime, parseApiDate } from '@shared/datetime';
import { checkInStatusMeta } from './components/statusMeta';

export type UsageLevel = 'ok' | 'warning' | 'over';

/** Usage meter state: warning from 80%, over at 100%. */
export function usageLevel(percent: number): UsageLevel {
  if (percent >= 100) return 'over';
  if (percent >= 80) return 'warning';
  return 'ok';
}

/** A check-in marker's accessible name. */
export function markerLabel(m: CheckInMarker): string {
  const status = checkInStatusMeta(m.status).label.toLowerCase();
  const proposals = m.proposals === 1 ? '1 proposal' : `${m.proposals} proposals`;
  const trial = m.trigger === 'trial' ? ' (trial)' : '';
  return `Check-in #${m.number}${trial}: ${status}, ${proposals}, ${formatDateTime(m.startedUtc)}`;
}

/** Compact age: "now", "5m", "2h", "3d". */
export function shortAge(utc: string, now: number = Date.now()): string {
  const t = parseApiDate(utc).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.max(0, Math.floor((now - t) / 60_000));
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

/** Time left until `utc`: "5h 20m", "2d 3h"; null when already past. */
export function timeLeft(utc: string, now: number = Date.now()): { text: string; hours: number } | null {
  const t = parseApiDate(utc).getTime();
  if (Number.isNaN(t) || t <= now) return null;
  const min = Math.floor((t - now) / 60_000);
  const hours = min / 60;
  if (min < 60) return { text: `${min}m`, hours };
  const h = Math.floor(min / 60);
  if (h < 24) return { text: `${h}h ${min % 60}m`, hours };
  return { text: `${Math.floor(h / 24)}d ${h % 24}h`, hours };
}

/** Admins and Owners approve, edit and allow tools to run on their own. */
export function canApprove(role: StandingMeta['role'] | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

export const APPROVER_ONLY_REASON = 'Only Admins and Owners can approve changes';
export const ACT_ONLY_REASON = 'Only Admins and Owners can allow a Pulsar to act on its own';

export type HaltFix = 'resume' | 'raiseLimit' | 'edit';

/**
 * The Halted banner in plain language, and the action that fixes it
 * (standing-agent-ui "Halted banner"). A limit is raised only by Admins/Owners;
 * a Member is told who can, and can still resume or edit.
 */
export function describeHalt(
  halt: { code: string; reason: string } | null | undefined,
  approver: boolean,
): { text: string; fixes: HaltFix[] } {
  switch (halt?.code) {
    case 'monthlyBudget':
      return {
        text: 'It stopped because it reached its monthly usage limit.' + (approver ? '' : ' An Admin or Owner can raise the limit.'),
        fixes: approver ? ['raiseLimit', 'resume'] : ['resume'],
      };
    case 'runaway':
      return {
        text: 'It stopped because two check-ins in a row went over the usage limit per check-in.' + (approver ? '' : ' An Admin or Owner can raise the limit.'),
        fixes: approver ? ['raiseLimit', 'resume'] : ['resume'],
      };
    case 'consecutiveFailures':
      return { text: 'It stopped because its last three check-ins failed. Check Activity for what went wrong, then resume.', fixes: ['resume', 'edit'] };
    case 'agentMissing':
      return { text: 'It stopped because the agent it uses was removed. Choose another agent in Settings.', fixes: ['edit'] };
    default:
      return { text: halt?.reason ? `It stopped: ${halt.reason}` : 'It stopped.', fixes: ['resume', 'edit'] };
  }
}
